import React, { useRef, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import iro from '@jaames/iro';
// import { EyeDropper } from 'react-eyedrop';
// import { EyeDropIcon } from 'components/icons';
import { ACCENT_COLOR, REGULARS, TERTIARY_COLOR } from 'utils';
import { Tooltip } from 'components/base/tooltip/Tooltip';
import { IClientFormFields } from 'modules/admin-panel/pages/clients/models';

// TODO add separate style file for the component
import 'modules/admin-panel/pages/clients/components/client-company-form/clientForm.styles.scss';

interface ColorBlockProps {
	title?: string;
	name?: string;
	wrapperClass?: string;
	defaultColour?: string;
	tooltipText?: string;
}

export const ColorBlock = ({
	title,
	name = IClientFormFields.color,
	wrapperClass = '',
	defaultColour = TERTIARY_COLOR,
	tooltipText,
}: ColorBlockProps) => {
	const { t: tr } = useTranslation('errors');
	const el = useRef<HTMLDivElement>(null);
	const colorPicker: React.MutableRefObject<iro.ColorPicker | null> = useRef<
		iro.ColorPicker
	>(null);

	const {
		register,
		setValue,
		errors,
		triggerValidation,
		watch,
	} = useFormContext();

	const [bgColor, setBgColor] = useState(defaultColour);

	const watchInput = watch(name);

	useEffect(() => {
		const colorChangeCallback = ({ hexString }: { hexString: string }) => {
			setValue(name, hexString);
		};

		if (!el.current) {
			return;
		}

		if (!colorPicker.current) {
			colorPicker.current = iro.ColorPicker(el.current, {
				color: bgColor,
				width: 100,
				layoutDirection: 'horizontal',
				sliderSize: 12,
				margin: 24,
				layout: [
					{
						component: iro.ui.Wheel,
					},
					{
						component: iro.ui.Slider,
						options: {
							handleRadius: 6,
						},
					},
				],
			});
			if (colorPicker.current) {
				colorPicker.current.on('input:end', colorChangeCallback);
			}
		}
		return () => {
			if (colorPicker.current) {
				colorPicker.current.off('input:end', colorChangeCallback);
			}
		};
	}, []);

	useEffect(() => {
		if (!watchInput || !watchInput.length || watchInput === '#') {
			setValue(name, bgColor);
			return;
		}

		if (watchInput.length === 1) {
			setValue(name, `#${watchInput}`);
			return;
		}

		triggerValidation(name).then((isCorrect) => {
			if (colorPicker.current && isCorrect) {
				colorPicker.current.color.hexString = watchInput;
				setBgColor(watchInput);
			}
		});
	}, [watchInput]);

	return (
		<div className={`col-2 flex-column ${wrapperClass}`}>
			<div className="form-block__title form-block__title-with-tooltip">
				{title}
				{tooltipText && (
					<Tooltip
						text={tooltipText}
						position="bottom-center"
						wrapperClass="partner-form__tooltip"
					/>
				)}
			</div>
			<div className="new-client-color row">
				<div ref={el} className="new-client-color__picker" />
				<div className="new-client-color__result">
					<div
						className="new-client-color__sample"
						// @ts-ignore
						style={{ background: bgColor }}
					/>
					<div className="new-client-color__input-wrapper">
						<input
							ref={register({
								required: {
									value: true,
									message: tr('required'),
								},
								pattern: {
									value: REGULARS.hexColor,
									message: tr('invalid_color_value'),
								},
							})}
							defaultValue={defaultColour}
							type="text"
							name={name}
							className="new-client-color__input"
							maxLength={7}
						/>
						{/* <EyeDropper
							onChange={handleEyeDropperChange}
							customComponent={EyeDropButton}
						/> */}
						{(errors?.color || errors?.color?.types?.message) && (
							<span className="new-client-color__error">
								{errors?.color?.message || errors?.color?.types?.message}
							</span>
						)}
					</div>
				</div>
			</div>
		</div>
	);

	/*
		It may need to add eyedropper in the future
	*/
	// const handleEyeDropperChange = ({ hex }: { hex: string }) => {
	// 	handleColorChange(hex);
	// };
};

// function EyeDropButton({ onClick }: { onClick: () => void }) {
// 	return (
// 		<Button
// 			buttonText=""
// 			onClick={onClick}
// 			icon={EyeDropIcon}
// 			buttonStyle="secondary"
// 			buttonClass="new-client-color__eyedrop"
// 		/>
// 	);
// }
